import { Injectable } from '@angular/core';
import { LoggerService } from 'src/app/core/services/logger.service';
import {
  CXoneDigitalContact,
  DigitalContactManager,
} from '@nice-devone/digital-sdk';
import { ChatModalService } from 'src/app/shared/services/chat-modal.service';
import { ContactInactivityTimer } from './contact-inactivity-timer.service';
import {
  ChatDirection,
  DigitalChatSession,
} from './digital-chat-session.service';
import { DigitalContactService } from '@nice-devone/digital-sdk/src/lib/digital/service/digital-contact-service';
import { PopUrlService } from 'src/app/data/CXOne/services/pop-url.service';
import {
  ChatModalButton,
  ChatModalButtonClick,
} from 'src/app/shared/components/chat-modal/chat-modal.component';
import { quickWebLogger } from '@quickweb/logger';
import { CxOneUtils } from 'src/app/data/CXOne/util/CxOneUtils';

const PREFIX = 'Timer -';

@Injectable()
export class DigitalChatSessionTimeoutManager {
  public static readonly CLOSE_MODAL_BUTTON_ID = 'close-chat';
  public static readonly ADD_TIME_BUTTON_ID = 'add-time';

  private readonly inactivityTimers: Record<string, ContactInactivityTimer> =
    {};
  private readonly chatSessionsByContactId: Record<string, DigitalChatSession> =
    {};
  constructor(
    private readonly logger: LoggerService,
    private readonly chatModalService: ChatModalService,
  ) {}

  public initEvents(digitalContactManager: DigitalContactManager) {
    digitalContactManager.onDigitalContactEvent.subscribe(
      async (digitalContact: CXoneDigitalContact) => {
        if (digitalContact?.eventDetails?.eventType === 'CaseInboxAssigned')
          return this.signalAssigned(
            digitalContactManager.digitalContactService,
            digitalContact,
          );

        if (digitalContact?.eventDetails?.eventType === 'MessageAddedIntoCase')
          return this.signalMessage(digitalContact);

        if (digitalContact?.eventDetails?.eventType !== 'CaseInboxUnassigned')
          return;

        this.signalClosed(digitalContact?.caseId);
      },
    );
  }

  public signalAssigned(
    digitalContactService: DigitalContactService,
    digitalContact: CXoneDigitalContact,
  ): void {
    if (!digitalContact?.caseId) return;

    const chatSession = new DigitalChatSession(
      digitalContactService,
      digitalContact,
    );

    this.logger.info(
      `${PREFIX} Interação do contato ${digitalContact?.caseId} atribuída.`,
    );

    this.chatSessionsByContactId[chatSession.contactId] = chatSession;
  }

  public signalMessage(digitalContact: CXoneDigitalContact): void {
    const msg = digitalContact.messages[digitalContact.messages.length - 1];
    const session = this.chatSessionsByContactId[digitalContact?.caseId];
    const timer = this.inactivityTimers[digitalContact?.caseId];
    const lastDirection = session?.lastDirection ?? null;

    session!.lastDirection = msg.direction as ChatDirection;

    if (!timer || digitalContact.messages.length < 1) {
      return;
    }

    if (session.lastDirection == 'inbound') {
      if (!timer.running) return;

      this.logger.info(
        `${PREFIX} Pausado para o contato ${digitalContact?.caseId}`,
      );
      this.hideModalForContact(digitalContact?.caseId ?? '');
      timer.resetAndPause();
      return;
    }

    if (!session.canIncreaseTimeout && lastDirection == 'outbound') return;

    this.hideModalForContact(digitalContact?.caseId ?? '');
    timer.reset();

    this.logger.info(
      lastDirection == 'outbound'
        ? `${PREFIX} Resetado para o contato ${digitalContact?.caseId}`
        : `${PREFIX} Timeout do chat ${digitalContact?.caseId} iniciado.`,
    );
  }

  public signalClosed(contactId: string): void {
    this.disposeAndRemoveInactivityTimer(contactId);
    delete this.chatSessionsByContactId[contactId];
    this.hideModalForContact(contactId);
    this.logger.info(`${PREFIX} Interação do contato ${contactId} fechada.`);
  }

  public initialize(
    popUrlService: PopUrlService,
    modal: ChatModalService,
  ): void {
    popUrlService.onReceiveData$.subscribe(async (popUrlData) => {
      const contactId = popUrlData.popUrl.contactId;
      const timeout = Number(popUrlData.data.u_json!.TIMEOUTTEMPO);
      const session = await this.waitSessionInitialized(contactId);
      this.logger.info(
        `${PREFIX} Recebido popUrl para o contato ${contactId} com timeout de ${timeout} segundos.`,
      );

      session.canIncreaseTimeout =
        popUrlData.data.u_json!.TIMEOUTATIVAMAISTEMPO?.toUpperCase() == 'S';

      if (typeof timeout === 'number' && timeout > 0) {
        this.configureTimer(contactId, timeout);
      } else {
        this.logger.info(
          `${PREFIX} Timeout não configurado para o contato ${contactId}`,
        );
      }
    });

    modal.buttonClick$.subscribe((button) => this.signalModalButton(button));

    this.logger.info(`${PREFIX} Inicializado com sucesso.`);
  }

  private signalModalButton(button: ChatModalButtonClick) {
    if (
      button.buttonId == DigitalChatSessionTimeoutManager.ADD_TIME_BUTTON_ID
    ) {
      const timer = this.inactivityTimers[button.metadata ?? ''];
      if (!timer) {
        this.logger.warn(
          `${PREFIX} Timer não encontrado para o contato ${button.metadata ?? ''}`,
        );
      } else {
        this.logger.info(
          `${PREFIX} Mais tempo foi solicitado para o contato ${button.metadata ?? ''}`,
        );
        timer.reset();
      }
    }

    this.chatModalService.hideModal(button.modalId);
  }

  public dispose(): void {
    Object.keys(this.inactivityTimers).forEach((contactId) => {
      this.inactivityTimers[contactId].dispose();
      delete this.inactivityTimers[contactId];
    });

    Object.keys(this.chatSessionsByContactId).forEach((contactId) => {
      delete this.chatSessionsByContactId[contactId];
    });

    this.chatModalService.hideAllModals();
  }

  private presentInactivityModal(
    contactId: string,
    message: string,
    buttons: ChatModalButton[],
  ): void {
    const modalId = this.getModalId(contactId);

    if (!this.chatModalService.exists(modalId)) {
      this.chatModalService.showModal(message, {
        id: modalId,
        showCloseButton: false,
        buttons,
      });
      return;
    }

    this.chatModalService.setMessage(message, modalId);
    this.chatModalService.setButtons(buttons, modalId);
    this.chatModalService.setShowCloseButton(false, modalId);
  }

  private hideModalForContact(contactId: string): void {
    const modalId = this.getModalId(contactId);

    if (this.chatModalService.exists(modalId))
      this.chatModalService.hideModal(modalId);
  }

  public configureTimer(contactId: string, timeout: number): void {
    const existingTimer = this.inactivityTimers[contactId];
    if (existingTimer) return;

    this.logger.info(
      `${PREFIX} Controlador de timeout inicializado para o contato ${contactId} e pronto para uso. A contagem de ${timeout} segundos será iniciada quando o processo for acionado.`,
    );

    this.inactivityTimers[contactId] = new ContactInactivityTimer(timeout, {
      onThresholdReached: (threshold) =>
        this.onInactivityThresholdReached(contactId, threshold),
      onRemainingSecondsChange: (remainingSeconds) =>
        this.onInactivityRemainingSecondsChanged(contactId, remainingSeconds),
      onExpired: () => {
        void this.closeChatByContactId(contactId);
      },
    });
  }

  private onInactivityThresholdReached(
    contactId: string,
    threshold: number,
  ): void {
    this.logger.info(
      `${PREFIX} Inatividade do chat ${contactId} atingiu ${threshold}% do tempo.`,
    );

    const timer = this.inactivityTimers[contactId];
    const session = this.chatSessionsByContactId[contactId];
    const remainingSeconds = timer?.remainingSeconds ?? 0;

    this.presentInactivityModal(
      contactId,
      this.buildInactivityCloseMessage(contactId, remainingSeconds),
      this.getModalButtons(contactId, session?.canIncreaseTimeout ?? false),
    );
  }

  private getModalButtons(
    contactId: string,
    maisTempo: boolean,
  ): ChatModalButton[] {
    const buttons: ChatModalButton[] = [
      {
        id: DigitalChatSessionTimeoutManager.CLOSE_MODAL_BUTTON_ID,
        label: 'Fechar',
      },
    ];

    if (maisTempo) {
      buttons.push({
        id: DigitalChatSessionTimeoutManager.ADD_TIME_BUTTON_ID,
        label: '+ tempo',
        metadata: contactId,
      });
    }

    return buttons;
  }

  private onInactivityRemainingSecondsChanged(
    contactId: string,
    remainingSeconds: number,
  ): void {
    this.chatModalService.setMessage(
      this.buildInactivityCloseMessage(contactId, remainingSeconds),
      this.getModalId(contactId),
    );
  }

  private getModalId(contactId: string): string {
    return `timeout-modal-${contactId}`;
  }

  private buildInactivityCloseMessage(
    contactId: string,
    remainingSecondsToClose: number,
  ): string {
    const chatSession = this.chatSessionsByContactId[contactId];
    const customerChannelName = chatSession?.customerName || contactId;

    return `A conversa com ${customerChannelName} está inativa há algum tempo. O chat será encerrado em ${remainingSecondsToClose} segundos.`;
  }

  private async closeChatByContactId(contactId: string): Promise<void> {
    const chatSession = this.chatSessionsByContactId[contactId];

    if (!chatSession) {
      this.logger.info(
        `${PREFIX} Não foi encontrada sessão para o contato ${contactId}.`,
      );
      this.disposeAndRemoveInactivityTimer(contactId);
      delete this.inactivityTimers[contactId];
      this.chatModalService.hideAllModals();
      return;
    }

    try {
      await chatSession.closeChannel();
      this.chatModalService.hideAllModals();
    } catch (error) {
      this.logger.error(
        `${PREFIX} Erro ao fechar o chat do contato ${contactId}`,
        error,
      );
      return;
    }

    this.disposeAndRemoveInactivityTimer(contactId);
    delete this.chatSessionsByContactId[contactId];
    const message = `${PREFIX} A conversa com ${chatSession.customerName} foi encerrada por inatividade.`;
    this.logger.info(message);

    quickWebLogger.logger.info(message, {
      contactId,
      type: chatSession.channelName,
    });
  }

  private disposeAndRemoveInactivityTimer(contactId: string): void {
    const timer = this.inactivityTimers[contactId];

    if (!timer) {
      return;
    }

    timer.dispose();
    delete this.inactivityTimers[contactId];
  }

  private async waitSessionInitialized(
    contactId: string,
  ): Promise<DigitalChatSession> {
    for (let index = 0; index < 5; index++) {
      const session = this.chatSessionsByContactId[contactId];
      if (session) return session;
      await CxOneUtils.Delay(1000);
    }

    throw new Error(
      `Session for contact ${contactId} not initialized after waiting.`,
    );
  }
}
